import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge, Table, Alert, Spinner } from 'react-bootstrap';
import { criarDistribuicaoWinback } from '../../services/api';
import './DistribuirClientes.css';

const DistribuirClientes = ({
    show,
    onHide,
    clientes = [],
    segmentoAtivo = null
}) => {
    const [quantidade, setQuantidade] = useState(10);
    const [vendedoraSelecionada, setVendedoraSelecionada] = useState('');
    const [clientesDistribuidos, setClientesDistribuidos] = useState([]);
    const [etapa, setEtapa] = useState(1); // 1: Configurar, 2: Visualizar, 3: Concluído
    const [copiado, setCopiado] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [distribuicaoSalva, setDistribuicaoSalva] = useState(null);

    // Lista de vendedoras (pode ser carregada de uma API no futuro)
    const vendedoras = [
        { id: 'vendedora1', nome: 'Vendedora 1' },
        { id: 'vendedora2', nome: 'Vendedora 2' },
        { id: 'vendedora3', nome: 'Vendedora 3' },
        { id: 'vendedora4', nome: 'Vendedora 4' },
        { id: 'todas', nome: 'Dividir entre todas' }
    ];

    useEffect(() => {
        if (show) {
            setEtapa(1);
            setClientesDistribuidos([]);
            setCopiado(false);
        }
    }, [show]);

    const handleDistribuir = () => {
        // Pegar os primeiros N clientes
        const clientesSelecionados = clientes.slice(0, Math.min(quantidade, clientes.length));

        // Se for dividir entre todas, adicionar qual vendedora
        if (vendedoraSelecionada === 'todas') {
            const vendedorasAtivas = vendedoras.filter(v => v.id !== 'todas');
            const distribuidos = clientesSelecionados.map((cliente, idx) => ({
                ...cliente,
                vendedora: vendedorasAtivas[idx % vendedorasAtivas.length].nome
            }));
            setClientesDistribuidos(distribuidos);
        } else {
            const vendedora = vendedoras.find(v => v.id === vendedoraSelecionada);
            const distribuidos = clientesSelecionados.map(cliente => ({
                ...cliente,
                vendedora: vendedora?.nome || 'Não atribuída'
            }));
            setClientesDistribuidos(distribuidos);
        }

        setEtapa(2);
    };

    const formatarTelefone = (telefone) => {
        if (!telefone) return '-';
        const num = telefone.replace(/\D/g, '');
        return num;
    };

    const gerarTextoWhatsApp = (cliente) => {
        const nome = cliente.contato_nome?.split(' ')[0] || 'Cliente';
        const dias = cliente.dias_sem_comprar || 0;
        return `Olá ${nome}! Tudo bem? Notamos que faz ${dias} dias que você não visita nossa loja. Temos novidades incríveis esperando por você! 🛍️`;
    };

    const copiarListaCompleta = () => {
        const texto = clientesDistribuidos.map(c =>
            `${c.contato_nome}\t${formatarTelefone(c.celular || c.telefone)}\t${c.cidade || '-'}\t${c.dias_sem_comprar} dias\t${c.vendedora}`
        ).join('\n');

        const header = 'Nome\tTelefone\tCidade\tDias Inativo\tVendedora\n';
        navigator.clipboard.writeText(header + texto);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    };

    const exportarCSV = () => {
        const header = 'Nome,Telefone,Email,Cidade,Dias Inativo,Total Gasto,Vendedora\n';
        const rows = clientesDistribuidos.map(c =>
            `"${c.contato_nome || ''}","${formatarTelefone(c.celular || c.telefone)}","${c.email || ''}","${c.cidade || ''}",${c.dias_sem_comprar || 0},${c.total_gasto || 0},"${c.vendedora}"`
        ).join('\n');

        const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `clientes_distribuidos_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleConcluir = async () => {
        setSalvando(true);
        try {
            // Salvar distribuição no banco para acompanhamento no funil
            const vendedoraNome = vendedoraSelecionada === 'todas'
                ? 'Dividido entre todas'
                : vendedoras.find(v => v.id === vendedoraSelecionada)?.nome || 'Não atribuída';

            const response = await criarDistribuicaoWinback(
                vendedoraNome,
                segmentoAtivo,
                clientesDistribuidos.map(c => ({
                    id: c.id || c.contato_id,
                    contato_nome: c.contato_nome,
                    celular: c.celular,
                    telefone: c.telefone,
                    email: c.email,
                    cidade: c.cidade,
                    dias_sem_comprar: c.dias_sem_comprar
                }))
            );

            if (response.success) {
                setDistribuicaoSalva(response.data);
            }
        } catch (error) {
            console.error('[DistribuirClientes] Erro ao salvar:', error);
        } finally {
            setSalvando(false);
            setEtapa(3);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    Distribuir Clientes
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Etapa 1: Configuração */}
                {etapa === 1 && (
                    <div>
                        <p className="text-muted mb-4">
                            Selecione quantos clientes deseja distribuir para as vendedoras entrarem em contato.
                        </p>

                        {segmentoAtivo && (
                            <Alert variant="info" className="mb-4">
                                <strong>Segmento ativo:</strong> {segmentoAtivo}
                            </Alert>
                        )}

                        <Row className="g-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Quantidade de Clientes</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        max={clientes.length}
                                        value={quantidade}
                                        onChange={(e) => setQuantidade(Math.min(Number(e.target.value), clientes.length))}
                                    />
                                    <Form.Text className="text-muted">
                                        Máximo disponível: {clientes.length} clientes
                                    </Form.Text>
                                </Form.Group>

                                {/* Atalhos de quantidade */}
                                <div className="d-flex gap-2 mt-2 flex-wrap">
                                    {[10, 25, 50, 100].map(n => (
                                        <Button
                                            key={n}
                                            variant={quantidade === n ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => setQuantidade(Math.min(n, clientes.length))}
                                        >
                                            {n}
                                        </Button>
                                    ))}
                                </div>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Atribuir para</Form.Label>
                                    <Form.Select
                                        value={vendedoraSelecionada}
                                        onChange={(e) => setVendedoraSelecionada(e.target.value)}
                                    >
                                        <option value="">Selecione uma vendedora...</option>
                                        {vendedoras.map(v => (
                                            <option key={v.id} value={v.id}>{v.nome}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="mt-4 p-3 bg-light rounded">
                            <h6 className="fw-semibold mb-2">Resumo</h6>
                            <p className="mb-0">
                                <strong>{quantidade}</strong> cliente{quantidade !== 1 ? 's' : ''} será(ão) distribuído(s)
                                {vendedoraSelecionada && (
                                    <>
                                        {' '}para <strong>
                                            {vendedoraSelecionada === 'todas'
                                                ? 'todas as vendedoras'
                                                : vendedoras.find(v => v.id === vendedoraSelecionada)?.nome
                                            }
                                        </strong>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                )}

                {/* Etapa 2: Visualização */}
                {etapa === 2 && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 className="mb-1">Lista de Distribuição</h6>
                                <small className="text-muted">
                                    {clientesDistribuidos.length} clientes selecionados
                                </small>
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="outline-primary" size="sm" onClick={copiarListaCompleta}>
                                    {copiado ? '✓ Copiado!' : 'Copiar Lista'}
                                </Button>
                                <Button variant="outline-success" size="sm" onClick={exportarCSV}>
                                    Exportar CSV
                                </Button>
                            </div>
                        </div>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table hover size="sm">
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th>#</th>
                                        <th>Cliente</th>
                                        <th>Telefone</th>
                                        <th>Cidade</th>
                                        <th>Dias</th>
                                        <th>Vendedora</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientesDistribuidos.map((cliente, idx) => (
                                        <tr key={idx}>
                                            <td className="text-muted">{idx + 1}</td>
                                            <td className="fw-medium">{cliente.contato_nome || '-'}</td>
                                            <td>
                                                {(cliente.celular || cliente.telefone) ? (
                                                    <a
                                                        href={`https://wa.me/55${formatarTelefone(cliente.celular || cliente.telefone)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-success text-decoration-none"
                                                    >
                                                        {formatarTelefone(cliente.celular || cliente.telefone)}
                                                    </a>
                                                ) : '-'}
                                            </td>
                                            <td>{cliente.cidade || '-'}</td>
                                            <td>
                                                <Badge bg={cliente.dias_sem_comprar > 90 ? 'danger' : cliente.dias_sem_comprar > 60 ? 'warning' : 'success'}>
                                                    {cliente.dias_sem_comprar}d
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg="secondary">{cliente.vendedora}</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}

                {/* Etapa 3: Concluído */}
                {etapa === 3 && (
                    <div className="text-center py-4">
                        <div className="mb-3" style={{ fontSize: '3rem' }}>✅</div>
                        <h5 className="fw-semibold mb-2">Distribuição Concluída!</h5>
                        <p className="text-muted">
                            {clientesDistribuidos.length} clientes foram preparados para contato.
                        </p>

                        {distribuicaoSalva && (
                            <Alert variant="info" className="mt-3 text-center">
                                <div className="mb-2">📊 <strong>Funil Ativado</strong></div>
                                <small>
                                    Os clientes foram adicionados ao funil de acompanhamento.
                                    Registre os resultados das ligações na aba "Funil".
                                </small>
                            </Alert>
                        )}

                        <Alert variant="success" className="mt-3 text-start">
                            <strong>📋 Próximos passos:</strong>
                            <ul className="mb-0 mt-2">
                                <li>Entre em contato com os clientes via WhatsApp ou telefone</li>
                                <li><strong>Registre o resultado</strong> de cada ligação no <Badge bg="primary">Funil de Feedback</Badge></li>
                                <li>Acompanhe as conversões e métricas em tempo real</li>
                                <li>Use os templates de mensagem para padronizar o contato</li>
                            </ul>
                        </Alert>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                {etapa === 1 && (
                    <>
                        <Button variant="outline-secondary" onClick={onHide}>
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleDistribuir}
                            disabled={!vendedoraSelecionada || quantidade < 1}
                        >
                            Gerar Lista
                        </Button>
                    </>
                )}
                {etapa === 2 && (
                    <>
                        <Button variant="outline-secondary" onClick={() => setEtapa(1)}>
                            Voltar
                        </Button>
                        <Button variant="success" onClick={handleConcluir}>
                            Concluir Distribuição
                        </Button>
                    </>
                )}
                {etapa === 3 && (
                    <Button variant="primary" onClick={onHide}>
                        Fechar
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default DistribuirClientes;
